
import React, { useState, useEffect, useMemo } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  SvgIcon,
  CircularProgress
} from '@mui/material';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend
} from 'chart.js';
import ASTITitle from '../../labels/ASTITitle';
import ASTIText from '../../labels/ASTIText';
import { fetchWithDemoFallback } from '../../../helpers/apiHelper';

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// Extracts a YYYY-MM bucket key from a transaction's date field, tolerating the
// several date formats used across the app (ISO, dd/mm/yyyy, yyyymmdd, etc.).
function getMonthKey(tx) {
  const raw = tx?.transactiondate_iso ?? tx?.transactiondate ?? tx?.Transactiedatum ?? tx?.date;
  const value = String(raw ?? '').trim();
  if (!value) return null;

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value.slice(0, 7);

  const ddmmyyyy = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, , mm, yyyy] = ddmmyyyy;
    return `${yyyy}-${mm}`;
  }

  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}`;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const yyyy = parsed.getFullYear();
  const mm = String(parsed.getMonth() + 1).padStart(2, '0');
  return `${yyyy}-${mm}`;
}

function formatMonthLabel(monthKey) {
  const [yyyy, mm] = monthKey.split('-');
  const date = new Date(Number(yyyy), Number(mm) - 1, 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
}

// Custom Delete Icon using SvgIcon
function DeleteSvgIcon(props) {
  return (
    <SvgIcon {...props} sx={{ color: '#2B1810' }}>
      <path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-4.5l-1-1z" />
    </SvgIcon>
  );
}

export default function CategoryMappingPage() {
  const [mappings, setMappings] = useState([]);
  const [keyword, setKeyword] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [allAvailableCategories, setAllAvailableCategories] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

// Fetch transactions on mount to compute monthly auto-categorization success rate

useEffect(() => {
  let isMounted = true;

  const fetchTransactionStats = async () => {
    setStatsLoading(true);
    setStatsError('');

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/transactions`, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error(`Failed to fetch transactions (HTTP ${response.status})`);
      }

      const data = await response.json();
      if (isMounted) {
        setTransactions(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      if (isMounted) {
        setStatsError(err?.message || 'Unable to load transactions for categorization stats.');
        setTransactions([]);
      }
    } finally {
      if (isMounted) setStatsLoading(false);
    }
  };

  fetchTransactionStats();
  return () => {
    isMounted = false;
  };
  }, []);

// Fetch all available categories (from categories table)
// Also refetch when window becomes visible (user returns from another section)
useEffect(() => {
  let isMounted = true;

  const fetchAvailableCategories = async () => {
    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/categories`, { cache: 'no-store' });
      if (response.ok && isMounted) {
        const cats = await response.json();
        setAllAvailableCategories(Array.isArray(cats) ? cats.sort() : []);
      }
    } catch (err) {
      console.error('Error fetching available categories:', err);
      if (isMounted) {
        setAllAvailableCategories([]);
      }
    }
  };

  // Initial fetch on component mount
  fetchAvailableCategories();

  // Refetch when page becomes visible (user returns from another section)
  const handleVisibilityChange = () => {
    if (!document.hidden) {
      fetchAvailableCategories();
    }
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);

  return () => {
    isMounted = false;
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
}, []);

const monthlyStats = useMemo(() => {
  const buckets = new Map();

  transactions.forEach((tx) => {
    const monthKey = getMonthKey(tx);
    if (!monthKey) return;

    if (!buckets.has(monthKey)) {
      buckets.set(monthKey, { 
        totalLines: 0, 
        categorizedLines: 0,
        totalEuros: 0,
        categorizedEuros: 0
      });
    }

    const bucket = buckets.get(monthKey);
    
    // Count by lines (transactions)
    bucket.totalLines += 1;
    
    // Count by euros (use absolute value to handle both income and expenses)
    const amount = Math.abs(Number(tx?.amount ?? 0));
    bucket.totalEuros += amount;

    const autoCategory = String(tx?.auto_category || '').trim();
    const isSuccessfullyCategorized = autoCategory.length > 0 && autoCategory.toLowerCase() !== 'other';
    if (isSuccessfullyCategorized) {
      bucket.categorizedLines += 1;
      bucket.categorizedEuros += amount;
    }
  });

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([monthKey, { totalLines, categorizedLines, totalEuros, categorizedEuros }]) => ({
      monthKey,
      label: formatMonthLabel(monthKey),
      totalLines,
      categorizedLines,
      rateByLines: totalLines > 0 ? (categorizedLines / totalLines) * 100 : 0,
      totalEuros,
      categorizedEuros,
      rateByEuros: totalEuros > 0 ? (categorizedEuros / totalEuros) * 100 : 0
    }));
}, [transactions]);

const chartData = useMemo(
  () => ({
    labels: monthlyStats.map((m) => m.label),
    datasets: [
      {
        label: 'Success rate by lines',
        data: monthlyStats.map((m) => Number(m.rateByLines.toFixed(1))),
        backgroundColor: '#C47A30',
        borderRadius: 4,
        maxBarThickness: 35
      },
      {
        label: 'Success rate by euros',
        data: monthlyStats.map((m) => Number(m.rateByEuros.toFixed(1))),
        backgroundColor: '#8B5A2B',
        borderRadius: 4,
        maxBarThickness: 35
      }
    ]
  }),
  [monthlyStats]
);

const chartOptions = useMemo(
  () => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const stat = monthlyStats[ctx.dataIndex];
            const datasetLabel = ctx.dataset.label;
            
            if (ctx.datasetIndex === 0) {
              // "Success rate by lines" - show line count
              return `${datasetLabel}: ${ctx.parsed.y}% (${stat.categorizedLines}/${stat.totalLines})`;
            } else if (ctx.datasetIndex === 1) {
              // "Success rate by euros" - show euro amount
              return `${datasetLabel}: ${ctx.parsed.y}% (€${stat.categorizedEuros.toFixed(2)}/€${stat.totalEuros.toFixed(2)})`;
            }
            return `${datasetLabel}: ${ctx.parsed.y}%`;
          }
        }
      }
    },
    scales: {
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value) => `${value}%`,
          color: '#7A5035'
        },
        grid: { color: '#F0E3D0' }
      },
      x: {
        ticks: { color: '#7A5035' },
        grid: { display: false }
      }
    }
  }),
  [monthlyStats]
);

useEffect(() => {
  const fetchMappings = async () => {
    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/keyw2cat`);

      if (!response.ok) {
        throw new Error('Failed to fetch keyw2cat data');
      }

      const data = await response.json();
      setMappings(data);
    } catch (error) {
      console.error('Error loading keyw2cat mappings:', error);
      const savedMappings = localStorage.getItem('categoryMappings');
      if (savedMappings) {
        setMappings(JSON.parse(savedMappings));
      }
    }
  };

  fetchMappings();
}, []);

   

  // Add new mapping
  const handleAddMapping = async () => {
    setError('');
    setStatus('');

    if (!keyword.trim()) {
      alert('Please enter a keyword.');
      return;
    }

    if (!category.trim()) {
      alert('Please select a category.');
      return;
    }

    // Validate that the category exists in existingCategories
    // Validate that the category exists in allAvailableCategories
    if (!allAvailableCategories.includes(category.trim())) {
      setError('The category must be one of the available categories. Please select from the dropdown.');
      return;
    }

    try {
      const response = await fetchWithDemoFallback(`${API_BASE}/api/keyw2cat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword: keyword.trim(), category: category.trim() })
      });

      if (!response.ok) {
        throw new Error('Failed to save mapping');
      }

      const saved = await response.json();
      setMappings([...mappings, saved]);
      setKeyword('');
      setCategory('');
      setStatus('Keyword mapping added successfully.');
      
      // Refetch transactions to update chart with new auto-categorization results
      try {
        const txResponse = await fetchWithDemoFallback(`${API_BASE}/api/transactions`, { cache: 'no-store' });
        if (txResponse.ok) {
          const updatedData = await txResponse.json();
          setTransactions(Array.isArray(updatedData) ? updatedData : []);
        }
      } catch (err) {
        console.error('Error refetching transactions:', err);
      }
    } catch (error) {
      console.error('Error saving mapping:', error);
      setError('Unable to save mapping to local database.');
    }
  };

  // Delete mapping
  const handleDeleteMapping = async (index) => {
    const mapping = mappings[index];
    if (!mapping?.id && !mapping?._id) {
      setMappings(mappings.filter((_, i) => i !== index));
      return;
    }

    try {
      const id = mapping.id || mapping._id;
      const response = await fetchWithDemoFallback(`${API_BASE}/api/keyw2cat/${id}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete mapping');
      }

      setMappings(mappings.filter((_, i) => i !== index));
      setStatus('Keyword mapping deleted.');
    } catch (error) {
      console.error('Error deleting mapping:', error);
      setError('Unable to delete mapping from local database.');
    }
  };

  return (
    <Box
      width="100vw"
      height="100vh"
      padding="20px"
      boxSizing="border-box"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="flex-start"
      sx={{ backgroundColor: '#FFFFFF', color: '#2B1810' }}
    >
      <ASTITitle text="Auto Categorization" />
      <ASTIText text="Define keywords and their corresponding categories." />

      <Box sx={{ width: '80%', mt: 2 }}>
        <ASTIText text="Auto-categorization success rate by month" />

        {statsLoading && (
          <Box display="flex" alignItems="center" gap={2} sx={{ color: '#2B1810', mt: 1 }}>
            <CircularProgress size={18} />
            Loading categorization stats…
          </Box>
        )}

        {!statsLoading && statsError && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {statsError}
          </Alert>
        )}

        {!statsLoading && !statsError && monthlyStats.length === 0 && (
          <Alert severity="info" sx={{ mt: 1 }}>
            No transaction data available yet.
          </Alert>
        )}

        {!statsLoading && !statsError && monthlyStats.length > 0 && (
          <Box sx={{ height: 260, mt: 1 }}>
            <Bar data={chartData} options={chartOptions} />
          </Box>
        )}
      </Box>

      {status && (
        <Alert severity="success" sx={{ mt: 2, width: '80%' }}>
          {status}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2, width: '80%' }}>
          {error}
        </Alert>
      )}

      {/* Input Fields */}
      <Box display="flex" gap="10px" marginTop="20px">
        <TextField
          label="Keyword"
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          size="small"
          sx={{
            input: { color: '#2B1810' },
            label: { color: '#7A5035' },
            '& .MuiOutlinedInput-root': {
              '& fieldset': { borderColor: '#D4B896' },
              '&:hover fieldset': { borderColor: '#C4A882' }
            }
          }}
        />
        <Autocomplete
          options={allAvailableCategories}
          value={category}
          onChange={(event, newValue) => setCategory(newValue || '')}
          inputValue={category}
          onInputChange={(event, newInputValue) => setCategory(newInputValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Category"
              sx={{
                input: { color: '#2B1810' },
                label: { color: '#7A5035' },
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: '#D4B896' },
                  '&:hover fieldset': { borderColor: '#C4A882' }
                }
              }}
            />
          )}
          sx={{
            minWidth: '200px',
            maxWidth: '300px',
            '& .MuiAutocomplete-root': {
              height: '56px'
            },
            '& .MuiOutlinedInput-root': {
              height: '100%'
            },
            '& .MuiAutocomplete-option': {
              fontSize: '14px',
              '&[aria-selected="true"]': { backgroundColor: 'rgba(244,167,185,0.3)' },
              '&.Mui-focused': { backgroundColor: '#F0E3D0' }
            },
            '& .MuiAutocomplete-clearIndicator': { color: '#7A5035' },
            '& .MuiAutocomplete-popupIndicator': { color: '#7A5035' }
          }}
          size="small"
        />
        <Button variant="contained" onClick={handleAddMapping} sx={{ backgroundColor: '#C47A30' }}>
          Add Mapping
        </Button>
      </Box>

      {/* Table of Mappings */}
      {mappings.length === 0 && (
        <ASTIText text="No classification mappings found in local database. Add a mapping above or import the old classification data." />
      )}

      {mappings.length > 0 && (
        <Table
          sx={{
            marginTop: '20px',
            width: '80%',
            backgroundColor: '#FFFFFF',
            color: '#2B1810',
            borderRadius: '8px'
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', color: '#2B1810' }}>Keyword</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#2B1810' }}>Category</TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: '#2B1810' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((mapping, index) => (
              <TableRow key={index}>
                <TableCell sx={{ color: '#2B1810' }}>{mapping.keyword}</TableCell>
                <TableCell sx={{ color: '#2B1810' }}>{mapping.category}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleDeleteMapping(index)}>
                    <DeleteSvgIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}